import React, { useEffect, useRef } from "react"
import ReactDOM from "react-dom"
import styled, { keyframes } from "styled-components"

import ButtonArrow, { primaryButtonArrowStyles } from "./../Buttons/ButtonArrow"
import { generatePath } from "./../../utils/helpers"

const steps = [
  "It starts with a kick-off seminar. Don't start blindly right away, but first understand the basics of training, nutrition, and recovery.",
  "After that, you train in small groups, with a maximum of six participants per coach. Personal guidance, surrounded by the energy of a group.",
  "The program builds up. In the first few weeks, you lay a foundation; after that, the intensity increases. Results that stick, not just for a moment.",
  "You receive a nutrition plan from your food coach. No crash diet and no standard schedule. A solide plan that fits into your life.",
  "You are never alone. Coaching and feedback via WhatsApp, email, and every training session; for the entire six weeks.",
]

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`

const riseIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(24px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const Overlay = styled.div`
  position: fixed;
  z-index: 20;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 16px;

  background-color: rgba(0, 0, 0, 0.82);

  animation: ${fadeIn} 0.25s ease-out both;

  ${props => props.theme.above.desktop`
        padding: 32px;
    `}
`

const Dialog = styled.div`
  position: relative;

  box-sizing: border-box;
  width: 100%;
  max-width: 1180px;
  max-height: calc(100vh - 32px);

  padding: 64px 24px 32px;

  border: 1px solid rgba(224, 226, 228, 0.2);
  background-color: ${props => props.theme.colors.dark};
  color: ${props => props.theme.colors.light};

  overflow-y: auto;
  overscroll-behavior: contain;

  animation: ${riseIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;

  &:before {
    content: "";

    position: absolute;
    top: 0;
    left: 0;

    width: 96px;
    height: 5px;

    background-color: ${props => props.theme.colors.orange};
  }

  ${props => props.theme.above.desktop`
        max-height: calc(100vh - 64px);
        padding: 72px 64px 56px;
    `}
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;
  padding: 0;

  border: 1px solid rgba(224, 226, 228, 0.35);
  border-radius: 50%;
  background: transparent;
  color: ${props => props.theme.colors.light};

  font-family: ${props => props.theme.fontFamilies.nbRegular};
  font-size: 24px;
  line-height: 1;

  cursor: pointer;
  transition: color 0.2s ease-out, border-color 0.2s ease-out,
    transform 0.2s ease-out;

  &:hover,
  &:focus {
    border-color: ${props => props.theme.colors.orange};
    color: ${props => props.theme.colors.orange};
    outline: none;
    transform: rotate(90deg);
  }

  ${props => props.theme.above.desktop`
        top: 24px;
        right: 24px;
        width: 44px;
        height: 44px;
    `}
`

const Content = styled.div`
  display: grid;
  gap: 48px;

  ${props => props.theme.above.desktop`
        grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
        gap: 72px;
    `}
`

const Introduction = styled.div`
  ${props => props.theme.above.desktop`
        position: sticky;
        top: 0;
        align-self: start;
    `}
`

const Eyebrow = styled.p`
  margin-bottom: 16px;

  color: ${props => props.theme.colors.orange};

  font-family: ${props => props.theme.fontFamilies.nbRegular};
  font-size: 12px;
  letter-spacing: 0.12em;
  line-height: 1.3;
  text-transform: uppercase;
`

const Title = styled.h2`
  max-width: 720px;
  margin-bottom: 24px;

  font-family: ${props => props.theme.fontFamilies.nbBold};
  font-size: 38px;
  line-height: 0.96;
  text-transform: uppercase;

  ${props => props.theme.above.desktop`
        font-size: 50px;
    `}
`

const Intro = styled.p`
  max-width: 560px;
  margin-bottom: 32px;

  color: ${props => props.theme.colors.silver};

  font-family: ${props => props.theme.fontFamilies.plainRegular};
  font-size: 18px;
  line-height: 1.55;
`

const StyledButton = styled(ButtonArrow)`
  ${primaryButtonArrowStyles}
`

const SectionTitle = styled.h3`
  margin-bottom: 28px;

  font-family: ${props => props.theme.fontFamilies.nbBold};
  font-size: 28px;
  line-height: 1;
  text-transform: uppercase;

  ${props => props.theme.above.desktop`
        font-size: 36px;
    `}
`

const StepList = styled.ol`
  margin: 0;
  padding: 0;

  list-style: none;
`

const Step = styled.li`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 16px;

  padding: 20px 0;

  border-top: 1px solid rgba(224, 226, 228, 0.18);

  &:last-child {
    border-bottom: 1px solid rgba(224, 226, 228, 0.18);
  }

  ${props => props.theme.above.desktop`
        grid-template-columns: 44px minmax(0, 1fr);
        gap: 20px;
        padding: 22px 0;
    `}
`

const StepNumber = styled.span`
  padding-top: 3px;

  color: ${props => props.theme.colors.orange};

  font-family: ${props => props.theme.fontFamilies.nbRegular};
  font-size: 12px;
  letter-spacing: 0.08em;
`

const StepText = styled.p`
  color: ${props => props.theme.colors.silver};

  font-family: ${props => props.theme.fontFamilies.plainRegular};
  font-size: 16px;
  line-height: 1.55;
`

const Closing = styled.p`
  margin-top: 28px;
  padding-left: 20px;

  border-left: 3px solid ${props => props.theme.colors.orange};

  font-family: ${props => props.theme.fontFamilies.plainRegular};
  font-size: 17px;
  line-height: 1.55;
`

const BootcampInfo = ({
  isOpen,
  onClose,
  triggerRef,
  lang,
  ctaLabel = "Join now",
  ctaSlug = "join-us",
  ctaModal,
}) => {
  const dialogRef = useRef(null)
  const closeButtonRef = useRef(null)
  const ctaPath = generatePath(lang, ctaSlug)

  useEffect(() => {
    if (!isOpen) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    closeButtonRef.current && closeButtonRef.current.focus()

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        onClose()
        return
      }

      if (event.key !== "Tab" || !dialogRef.current) return

      const focusableElements = dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("keydown", handleKeyDown)
      triggerRef.current && triggerRef.current.focus()
    }
  }, [isOpen, onClose, triggerRef])

  if (!isOpen || typeof document === "undefined") return null

  return ReactDOM.createPortal(
    <Overlay
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bootcamp-info-title"
      >
        <CloseButton
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label="Close Bootcamp information"
        >
          ×
        </CloseButton>

        <Content>
          <Introduction>
            <Eyebrow>Rockstar Bootcamp · 6 weeks</Eyebrow>
            <Title id="bootcamp-info-title">
              Stop following. Start understanding.
            </Title>
            <Intro>
              Most training programs tell you what to do. At the Rockstar
              Bootcamp, after six weeks, you will also understand why. That is
              exactly why people stick with it, and why over 3,500 participants
              have joined in eleven years.
            </Intro>
            <StyledButton
              label={ctaLabel}
              to={ctaPath}
              modal={ctaModal}
              onClick={onClose}
            />
          </Introduction>

          <div>
            <SectionTitle>How does the bootcamp work?</SectionTitle>
            <StepList>
              {steps.map((step, index) => (
                <Step key={step}>
                  <StepNumber>{String(index + 1).padStart(2, "0")}</StepNumber>
                  <StepText>{step}</StepText>
                </Step>
              ))}
            </StepList>
            <Closing>
              Stronger, fitter, sharper. If it doesn't work, you get your money
              back. That promise has stood since 2014. We work with small
              groups, so spots are limited. Be sure to sign up early.
            </Closing>
          </div>
        </Content>
      </Dialog>
    </Overlay>,
    document.body
  )
}

export default BootcampInfo
